import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition
} from '@headlessui/react'
import {
  Bars2Icon,
  Cog6ToothIcon,
  LockClosedIcon,
  ViewColumnsIcon
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { MotionConfig, Reorder, useDragControls } from 'framer-motion'
import PropTypes from 'prop-types'
import { Fragment, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import trackEvents from '../../constants/track-events'
import { useUserContext } from '../../context'
import { trackEvent } from '../../helpers/analytics'
import { ROLES } from '../../helpers/authorization-roles'
import Badge from '../tailwind/Badge'
import Button from '../tailwind/Button'

const I18N = 'react_table.column_settings.'

const KEY_MOVES = { ArrowUp: -1, ArrowDown: 1, Home: 'first', End: 'last' }

// Ab so vielen Zusatzfeldern lohnt sich ein Suchfeld.
const ADDITIONAL_SEARCH_THRESHOLD = 7

const ColumnRow = ({ tableId, item, index, onToggle, onMove, onDragEnd }) => {
  const { t } = useTranslation()
  const controls = useDragControls()
  const dragStartIndexRef = useRef(index)
  const inputId = `column-settings-${tableId}-${item.id}`

  const handleKeyDown = (event) => {
    const delta = KEY_MOVES[event.key]
    if (delta === undefined) return

    event.preventDefault()
    onMove(item.id, delta)
  }

  return (
    <Reorder.Item
      as='li'
      value={item.id}
      dragListener={false}
      dragControls={controls}
      whileDrag={{
        scale: 1.02,
        boxShadow:
          '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
      }}
      onDragStart={() => {
        dragStartIndexRef.current = index
      }}
      onDragEnd={() => onDragEnd(item.id, dragStartIndexRef.current, index)}
      className='group flex items-center gap-x-2 rounded-md bg-white px-2 py-1.5 hover:bg-gray-50'
    >
      <button
        type='button'
        aria-label={t(I18N + 'move_column', { column: item.label })}
        onPointerDown={(event) => {
          event.preventDefault()
          controls.start(event)
        }}
        onKeyDown={handleKeyDown}
        className='-ml-0.5 flex size-6 shrink-0 cursor-grab touch-none items-center justify-center rounded text-gray-400 hover:text-gray-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-600 active:cursor-grabbing'
      >
        <Bars2Icon className='size-4' aria-hidden='true' />
      </button>
      <input
        id={inputId}
        type='checkbox'
        checked={item.visible}
        onChange={() => onToggle(item.id)}
        className='h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-1 focus:ring-blue-600'
      />
      <label
        htmlFor={inputId}
        title={item.label}
        className={clsx(
          'flex-1 cursor-pointer select-none truncate text-sm',
          item.visible ? 'text-gray-900' : 'text-gray-500'
        )}
      >
        {item.label}
      </label>
    </Reorder.Item>
  )
}

ColumnRow.propTypes = {
  tableId: PropTypes.string.isRequired,
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    visible: PropTypes.bool.isRequired
  }).isRequired,
  index: PropTypes.number.isRequired,
  onToggle: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired
}

const ColumnSettings = ({ tableId, settings }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { activeSpace } = useUserContext()
  const [announcement, setAnnouncement] = useState('')
  const [additionalQuery, setAdditionalQuery] = useState('')

  // Die partnerweite Grundlage ändern nur Admins, alle anderen sehen den Hinweis.
  const canManageDefaults =
    activeSpace?.authorization_role?.id === ROLES.ADMIN.id

  const {
    items,
    additionalItems = [],
    disabledItems = [],
    primary,
    hiddenCount,
    isCustomized,
    toggleColumn,
    reorderColumns,
    moveColumn,
    showAll,
    reset
  } = settings

  const labelFor = (id) => items.find((item) => item.id === id)?.label || id

  const handleToggle = (id) => {
    const item = items.find((candidate) => candidate.id === id)
    toggleColumn(id)
    trackEvent(trackEvents.TOGGLE_TABLE_COLUMN, {
      table: tableId,
      column: id,
      visible: item ? !item.visible : true
    })
  }

  const handleAdd = (item) => {
    toggleColumn(item.id)
    trackEvent(trackEvents.TOGGLE_TABLE_COLUMN, {
      table: tableId,
      column: item.id,
      visible: true,
      additional: true
    })
    setAnnouncement(t(I18N + 'additional_added', { column: item.label }))
  }

  const filteredAdditionalItems = useMemo(() => {
    const needle = additionalQuery.trim().toLowerCase()
    if (!needle) return additionalItems

    return additionalItems.filter((item) =>
      item.label.toLowerCase().includes(needle)
    )
  }, [additionalItems, additionalQuery])

  const handleMove = (id, delta) => {
    const moved = moveColumn(id, delta)
    if (!moved) return

    trackEvent(trackEvents.REORDER_TABLE_COLUMNS, {
      table: tableId,
      column: id,
      from: moved.from,
      to: moved.to
    })
    setAnnouncement(
      t(I18N + 'position_announcement', {
        column: labelFor(id),
        position: moved.to + 1,
        total: items.length
      })
    )
  }

  // onReorder feuert pro Swap, getrackt wird nur das Ergebnis eines Drags.
  const handleDragEnd = (id, from, to) => {
    setAnnouncement('')
    if (from === to) return

    trackEvent(trackEvents.REORDER_TABLE_COLUMNS, {
      table: tableId,
      column: id,
      from,
      to
    })
  }

  const handleReset = () => {
    reset()
    setAnnouncement('')
    trackEvent(trackEvents.RESET_TABLE_COLUMNS, { table: tableId })
  }

  const handleShowAll = () => {
    showAll()
    trackEvent(trackEvents.SHOW_ALL_TABLE_COLUMNS, { table: tableId })
  }

  const handleManageDefaults = () => {
    trackEvent(trackEvents.OPEN_TABLE_COLUMN_DEFAULTS, { table: tableId })
    navigate('/settings/dashboard')
  }

  const allHidden = items.length > 0 && hiddenCount === items.length

  return (
    <Popover className='relative inline-block text-left'>
      {({ open }) => (
        <>
          <PopoverButton
            id={`column-settings-${tableId}`}
            onClick={() => {
              if (open) return
              trackEvent(trackEvents.OPEN_TABLE_COLUMN_SETTINGS, {
                table: tableId,
                hidden_count: hiddenCount
              })
            }}
            className='group inline-flex items-center rounded-md p-2 transition duration-150 ease-out hover:bg-slate-100 hover:ease-in focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-600 data-[open]:bg-slate-100'
          >
            <ViewColumnsIcon
              className='size-5 shrink-0 text-gray-700 group-hover:text-gray-900'
              aria-hidden='true'
            />
            <span className='ml-1.5 whitespace-nowrap text-sm font-medium text-gray-900'>
              {t(I18N + 'button')}
            </span>
            {hiddenCount > 0 && (
              <span className='ml-1.5 rounded bg-gray-200 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-gray-900'>
                <span aria-hidden='true'>{hiddenCount}</span>
                <span className='sr-only'>
                  {t(I18N + 'hidden_count_sr', { count: hiddenCount })}
                </span>
              </span>
            )}
          </PopoverButton>

          <Transition
            as={Fragment}
            enter='transition ease-out duration-100'
            enterFrom='transform opacity-0 scale-95'
            enterTo='transform opacity-100 scale-100'
            leave='transition ease-in duration-75'
            leaveFrom='transform opacity-100 scale-100'
            leaveTo='transform opacity-0 scale-95'
          >
            <PopoverPanel className='absolute right-0 z-30 mt-2 w-80 origin-top-right rounded-md border bg-white p-3 shadow-2xl'>
              <div className='flex items-center justify-between gap-x-2 px-2'>
                <p className='text-sm font-semibold text-gray-900'>
                  {t(I18N + 'title')}
                </p>
                <Badge
                  type='basic'
                  color='gray'
                  text={t(I18N + 'scope_badge')}
                />
              </div>
              <p className='mt-0.5 px-2 text-xs text-gray-500'>
                {t(I18N + 'hint')}
              </p>

              <ul className='mt-2'>
                {primary && (
                  <li className='flex items-center gap-x-2 rounded-md px-2 py-1.5'>
                    <span
                      aria-hidden='true'
                      className='-ml-0.5 size-6 shrink-0'
                    />
                    <span className='flex-1 truncate text-sm text-gray-900'>
                      {primary.label}
                    </span>
                    <span title={t(I18N + 'always_visible')}>
                      <LockClosedIcon
                        className='size-4 text-gray-400'
                        aria-hidden='true'
                      />
                      <span className='sr-only'>
                        {t(I18N + 'always_visible')}
                      </span>
                    </span>
                  </li>
                )}
              </ul>

              <MotionConfig reducedMotion='user'>
                <Reorder.Group
                  as='ul'
                  axis='y'
                  values={items.map((item) => item.id)}
                  onReorder={reorderColumns}
                  className='select-none space-y-0.5'
                >
                  {items.map((item, index) => (
                    <ColumnRow
                      key={item.id}
                      tableId={tableId}
                      item={item}
                      index={index}
                      onToggle={handleToggle}
                      onMove={handleMove}
                      onDragEnd={handleDragEnd}
                    />
                  ))}
                </Reorder.Group>
              </MotionConfig>

              {allHidden && (
                <p className='px-2 pt-2 text-xs text-gray-500'>
                  {t(I18N + 'all_hidden_hint')}
                </p>
              )}

              {additionalItems.length > 0 && (
                <div className='mt-3 border-t border-gray-100 pt-3'>
                  <p className='px-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400'>
                    {t(I18N + 'additional_group')}
                  </p>
                  <p className='mt-0.5 px-2 text-xs text-gray-500'>
                    {t(I18N + 'additional_hint')}
                  </p>
                  {additionalItems.length >= ADDITIONAL_SEARCH_THRESHOLD && (
                    <div className='mt-2 px-2'>
                      <input
                        type='search'
                        value={additionalQuery}
                        onChange={(event) =>
                          setAdditionalQuery(event.target.value)
                        }
                        placeholder={t(I18N + 'additional_search_placeholder')}
                        aria-label={t(I18N + 'additional_search_placeholder')}
                        className='block w-full rounded-md border-0 px-2 py-1.5 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-blue-600'
                      />
                    </div>
                  )}
                  <ul className='mt-1 max-h-56 space-y-0.5 overflow-y-auto'>
                    {filteredAdditionalItems.map((item) => {
                      const inputId = `column-settings-${tableId}-add-${item.id}`

                      return (
                        <li
                          key={item.id}
                          className='flex items-start gap-x-2 rounded-md px-2 py-1.5 hover:bg-gray-50'
                        >
                          <span
                            aria-hidden='true'
                            className='-ml-0.5 size-6 shrink-0'
                          />
                          <input
                            id={inputId}
                            type='checkbox'
                            checked={false}
                            onChange={() => handleAdd(item)}
                            className='mt-0.5 h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-1 focus:ring-blue-600'
                          />
                          <label
                            htmlFor={inputId}
                            title={item.label}
                            className='line-clamp-2 flex-1 cursor-pointer select-none break-words text-sm text-gray-500 hover:text-gray-900'
                          >
                            {item.label}
                          </label>
                        </li>
                      )
                    })}
                    {!filteredAdditionalItems.length && (
                      <li className='px-2 py-1.5 text-xs text-gray-500'>
                        {t(I18N + 'additional_no_results')}
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {disabledItems.length > 0 && (
                <div className='mt-3'>
                  <p className='px-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400'>
                    {t(I18N + 'disabled_group')}
                  </p>
                  <ul className='mt-1 space-y-0.5'>
                    {disabledItems.map((item) => (
                      <li
                        key={item.id}
                        className='flex items-center gap-x-2 rounded-md px-2 py-1.5'
                      >
                        <span
                          aria-hidden='true'
                          className='-ml-0.5 flex size-6 shrink-0 items-center justify-center text-gray-300'
                        >
                          <LockClosedIcon className='size-4' />
                        </span>
                        <input
                          type='checkbox'
                          disabled
                          defaultChecked={false}
                          aria-label={`${item.label}: ${t(I18N + 'disabled_group')}`}
                          className='h-4 w-4 rounded border-gray-200 text-gray-300'
                        />
                        <span className='flex-1 truncate text-sm text-gray-400'>
                          {item.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className='mt-2 flex items-center justify-between border-t border-gray-100 px-2 pt-2'>
                <Button.Text
                  text={t(I18N + 'reset_action')}
                  onClick={handleReset}
                  disabled={!isCustomized}
                  minWidth=''
                />
                {hiddenCount > 0 && (
                  <Button.Text
                    text={t(I18N + 'show_all_action')}
                    onClick={handleShowAll}
                    minWidth=''
                  />
                )}
              </div>

              {(canManageDefaults || disabledItems.length > 0) && (
                <div className='mt-2 border-t border-gray-100 px-2 pt-2'>
                  {canManageDefaults ? (
                    <button
                      type='button'
                      onClick={handleManageDefaults}
                      className='inline-flex items-center gap-x-1.5 text-xs font-medium text-gray-500 hover:text-gray-900 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-600'
                    >
                      <Cog6ToothIcon className='size-4' aria-hidden='true' />
                      {t(I18N + 'manage_defaults_action')}
                    </button>
                  ) : (
                    <p className='text-xs text-gray-500'>
                      {t(I18N + 'disabled_hint')}
                    </p>
                  )}
                </div>
              )}
            </PopoverPanel>
          </Transition>

          {/* Lebt außerhalb des Panels: eine beim Öffnen neu eingefügte Region mit
              altem Text würde erneut vorgelesen. */}
          <div aria-live='polite' className='sr-only'>
            {announcement}
          </div>
        </>
      )}
    </Popover>
  )
}

ColumnSettings.propTypes = {
  tableId: PropTypes.string.isRequired,
  settings: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        visible: PropTypes.bool.isRequired
      })
    ).isRequired,
    additionalItems: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired
      })
    ),
    disabledItems: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired
      })
    ),
    primary: PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    }),
    hiddenCount: PropTypes.number.isRequired,
    isCustomized: PropTypes.bool.isRequired,
    toggleColumn: PropTypes.func.isRequired,
    reorderColumns: PropTypes.func.isRequired,
    moveColumn: PropTypes.func.isRequired,
    showAll: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired
  }).isRequired
}

export default ColumnSettings
